import { useInfiniteQuery, useQuery, type InfiniteData, type UseInfiniteQueryResult } from '@tanstack/react-query';
import { ArrowDownLeft, ArrowUpRight, Phone, PhoneMissed, Sparkles, Users } from 'lucide-react';
import { Fragment, useId, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PeoplePicker } from '../components/PeoplePicker';
import { FormPage } from '../components/FormPage';
import { callErrorText } from '../components/CallDock';
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, LoadingBlock, PageHeader, Select, Table, Td, Th, Tr, Tabs, useToast } from '../components/ui';
import { api } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useCallSession } from '../lib/call-session';
import {
  OUTCOME_LABEL,
  OUTCOME_TONE,
  RECORDING_LABEL,
  RECORDING_TONE,
  callClock,
  callsApi,
  isClientParty,
  useCallPeople,
  useCallsStatus,
  type CallPage,
  type CallStatus,
  type CallSummary,
  listSeparator,
} from '../lib/calls';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime } from '../lib/format';

/**
 * Calls (2026-10-29): New call, My history, and — for whoever oversees them —
 * the clients' calls from the portal, the calls between employees, and every
 * call (operator, 2026-11-01: "make two tabs clients calls history - employees
 * call history - my history - All calls of course check permissions who can see
 * this"). Every call is recorded; who was really connected to one plays its whole
 * recording from its page, and whoever oversees its kind any.
 */
type Tab = 'mine' | 'clients' | 'employees' | 'all';
type Filter = 'all' | 'missed' | 'outgoing' | 'incoming';

/** Each tab a reader oversees, and the list it reads. */
const OVERSEEN: Record<Exclude<Tab, 'mine'>, { path: string; empty: TranslationKey }> = {
  clients: { path: '/calls/client-calls', empty: 'calls.noClientCalls' },
  employees: { path: '/calls/employee-calls', empty: 'calls.noneMatch' },
  all: { path: '/calls/all', empty: 'calls.noneMatch' },
};

const STATUSES: CallStatus[] = ['COMPLETED', 'MISSED', 'REJECTED', 'CANCELLED', 'BUSY', 'FAILED', 'ACTIVE', 'RINGING'];

export function CallsPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // My history for everyone; each other tab for whoever oversees it — calls.view_all oversees them all.
  const seesAll = can(P.CALLS_VIEW_ALL);
  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'mine', label: t('calls.myHistory') },
    ...(seesAll || can(P.CALLS_VIEW_CLIENT_CALLS) ? [{ id: 'clients' as const, label: t('calls.clientCalls') }] : []),
    ...(seesAll || can(P.CALLS_VIEW_EMPLOYEE_CALLS) ? [{ id: 'employees' as const, label: t('calls.employeeCalls') }] : []),
    ...(seesAll ? [{ id: 'all' as const, label: t('calls.allCalls') }] : []),
  ];
  const wanted = params.get('tab');
  const tab: Tab = tabs.find((x) => x.id === wanted)?.id ?? 'mine';
  const { data: status } = useCallsStatus();

  return (
    <>
      <PageHeader
        title={t('calls.title')}
        subtitle={t('calls.subtitle')}
        action={
          <Button variant="primary" disabled={status ? !status.available : true} onClick={() => navigate('/calls/new')}>
            <Phone className="h-4 w-4" aria-hidden />
            {t('calls.newCall')}
          </Button>
        }
      />
      {status && !status.available && <UnavailableNote reasons={status.reasons} missing={status.missing} />}
      {tabs.length > 1 && (
        <Tabs active={tab} onChange={(id) => setParams(id === 'mine' ? {} : { tab: id }, { replace: true })} tabs={tabs} />
      )}
      <div className={tabs.length > 1 ? 'mt-5' : undefined}>
        {/* Keyed: each tab its own filters, never another's carried over. */}
        {tab === 'mine' ? <MyCalls /> : <OverseenCalls key={tab} scope={tab} />}
      </div>
    </>
  );
}

function UnavailableNote({ reasons, missing }: { reasons: string[]; missing: string[] }) {
  const { t } = useI18n();
  return (
    <Card className="mb-5 border-s-4 border-warning">
      <p className="text-body-sm text-[var(--text-primary)]">
        {reasons.includes('CALLS_DISABLED') ? t('calls.unavailableOff') : t('calls.unavailableNotReady')}
      </p>
      {missing.length > 0 && (
        <p className="mt-1 text-caption text-muted" dir="ltr">
          {t('calls.unavailableMissing')} {missing.join(', ')}
        </p>
      )}
    </Card>
  );
}

function MyCalls() {
  const { t } = useI18n();
  const [filter, setFilter] = useState<Filter>('all');
  const query = useInfiniteQuery({
    queryKey: ['calls', 'history', filter],
    queryFn: ({ pageParam }) => api.get<CallPage>('/calls/history', { filter, cursor: pageParam ?? undefined, limit: 25 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 30_000,
  });
  return (
    <Card padded={false}>
      <div className="flex flex-wrap gap-2 border-b hairline p-3">
        {(['all', 'missed', 'outgoing', 'incoming'] as Filter[]).map((f) => (
          <Button key={f} size="sm" variant={filter === f ? 'primary' : 'secondary'} aria-pressed={filter === f} onClick={() => setFilter(f)}>
            {t(`calls.filter.${f}` as TranslationKey)}
          </Button>
        ))}
      </div>
      <CallTable query={query} empty={t('calls.noneYet')} />
    </Card>
  );
}

/**
 * The calls a reader oversees without being on them: the clients' calls from the portal, the calls between
 * employees, or every call — filtered by employee, client, status, type and dates.
 */
function OverseenCalls({ scope }: { scope: Exclude<Tab, 'mine'> }) {
  const { t, localized } = useI18n();
  const { data: people } = useCallPeople();
  const { user, can } = useAuth();
  // The clients to filter by, for whoever may list them; the client calls tab and All calls only.
  const withClients = scope !== 'employees';
  const clients = useQuery({
    queryKey: ['client-options'],
    queryFn: () => api.get<Array<{ id: string; companyName: string; companyNameAr: string | null }>>('/clients/options'),
    enabled: withClients && (can(P.CLIENTS_VIEW) || can(P.TICKETS_CREATE) || can(P.TICKETS_HANDLE)),
    staleTime: 5 * 60_000,
  });
  const [userId, setUserId] = useState('');
  const [clientId, setClientId] = useState('');
  const [status, setStatus] = useState<CallStatus | ''>('');
  const [type, setType] = useState<'' | 'ONE_TO_ONE' | 'GROUP'>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const everyone = useMemo(
    () => [...(people ?? []), ...(user ? [{ id: user.id, fullName: user.fullName, fullNameAr: user.fullNameAr }] : [])],
    [people, user],
  );
  const params = {
    userId: userId || undefined,
    clientId: (withClients && clientId) || undefined,
    status: status || undefined,
    type: type || undefined,
    from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
    to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
  };
  const { path, empty } = OVERSEEN[scope];
  const query = useInfiniteQuery({
    queryKey: ['calls', scope, params],
    queryFn: ({ pageParam }) => api.get<CallPage>(path, { ...params, cursor: pageParam ?? undefined, limit: 25 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const clientChoices = withClients ? (clients.data ?? []) : [];
  return (
    <Card padded={false}>
      <div className={'grid gap-3 border-b hairline p-3 sm:grid-cols-2 ' + (clientChoices.length ? 'lg:grid-cols-6' : 'lg:grid-cols-5')}>
        {clientChoices.length > 0 && (
          <Field label={t('calls.client')}>
            <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">{t('calls.anyClient')}</option>
              {clientChoices
                .map((c) => ({ id: c.id, name: localized({ name: c.companyName, nameAr: c.companyNameAr }) }))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </Select>
          </Field>
        )}
        <Field label={t('calls.employee')}>
          <Select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">{t('calls.everyone')}</option>
            {everyone
              .slice()
              .sort((a, b) => localized(a).localeCompare(localized(b)))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {localized(p)}
                </option>
              ))}
          </Select>
        </Field>
        <Field label={t('calls.status')}>
          <Select value={status} onChange={(e) => setStatus(e.target.value as CallStatus | '')}>
            <option value="">{t('calls.anyStatus')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`calls.callStatus.${s}` as TranslationKey)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('calls.type')}>
          <Select value={type} onChange={(e) => setType(e.target.value as typeof type)}>
            <option value="">{t('calls.anyType')}</option>
            <option value="ONE_TO_ONE">{t('calls.voiceCall')}</option>
            <option value="GROUP">{t('calls.groupVoiceCall')}</option>
          </Select>
        </Field>
        <Field label={t('calls.from')}>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label={t('calls.to')}>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </div>
      <CallTable query={query} empty={t(empty)} everyone />
    </Card>
  );
}

function CallTable({
  query,
  empty,
  everyone = false,
}: {
  query: UseInfiniteQueryResult<InfiniteData<CallPage, unknown>, Error>;
  empty: string;
  everyone?: boolean;
}) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  if (query.isLoading) return <LoadingBlock />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const rows = query.data?.pages.flatMap((p) => p.items) ?? [];
  if (rows.length === 0) return <EmptyState title={empty} hint={t('calls.recordedHint')} icon={<Phone className="h-7 w-7" />} />;
  // The caller first; a client's sign-in marked as the client (2026-11-01).
  const names = (c: CallSummary) => {
    const list = everyone && c.caller ? [c.caller, ...c.people.filter((p) => p.id !== c.caller!.id)] : c.people;
    if (list.length === 0) return '—';
    return list.map((p, i) => (
      <Fragment key={p.id}>
        {i > 0 && listSeparator(locale)}
        {localized(p)}
        {isClientParty(p) && (
          <Badge tone="info" className="ms-1.5 align-middle">
            {t('calls.clientBadge')}
          </Badge>
        )}
      </Fragment>
    ));
  };
  return (
    <>
      <Table>
        <thead>
          <tr>
            <Th>{everyone ? t('calls.people') : t('calls.with')}</Th>
            <Th>{t('calls.outcome')}</Th>
            <Th>{t('calls.when')}</Th>
            <Th align="end">{t('calls.duration')}</Th>
            <Th>{t('calls.recording')}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <Tr key={c.id} onClick={() => navigate(`/calls/${c.id}`)}>
              <Td>
                <span className="flex min-w-0 items-center gap-2">
                  <DirectionIcon call={c} />
                  {c.type === 'GROUP' && <Users className="h-3.5 w-3.5 shrink-0 text-muted" aria-label={t('calls.groupVoiceCall')} />}
                  <span className="truncate text-[var(--text-primary)]">{names(c)}</span>
                </span>
              </Td>
              <Td>
                <Badge tone={OUTCOME_TONE[c.outcome]}>{t(OUTCOME_LABEL[c.outcome])}</Badge>
              </Td>
              <Td className="whitespace-nowrap text-muted">{formatDateTime(c.createdAt, locale)}</Td>
              <Td align="end" className="tabular">
                {c.durationSeconds !== null ? callClock(c.durationSeconds) : '—'}
              </Td>
              <Td>
                <span className="inline-flex items-center gap-2">
                  {c.recording.shown === 'NONE' ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <Badge tone={RECORDING_TONE[c.recording.shown]}>{t(RECORDING_LABEL[c.recording.shown])}</Badge>
                  )}
                  {/* Its AI summary is there to read (never its text, in a list). */}
                  {c.ai?.ready && (
                    <span title={t('callAi.summaryReady')} className="inline-flex text-primary dark:text-primary-400">
                      <Sparkles className="h-4 w-4" aria-hidden />
                      <span className="sr-only">{t('callAi.summaryReady')}</span>
                    </span>
                  )}
                </span>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
      {query.hasNextPage && (
        <div className="flex justify-center border-t hairline p-3">
          <Button size="sm" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
            {t('calls.loadMore')}
          </Button>
        </div>
      )}
    </>
  );
}

function DirectionIcon({ call }: { call: CallSummary }) {
  const { t } = useI18n();
  if (call.outcome === 'MISSED') return <PhoneMissed className="h-4 w-4 shrink-0 text-error-fg dark:text-error-dark" aria-label={t('calls.outcome.MISSED')} />;
  if (call.direction === 'OUTGOING') return <ArrowUpRight className="h-4 w-4 shrink-0 text-muted flip-rtl" aria-label={t('calls.outgoing')} />;
  if (call.direction === 'INCOMING') return <ArrowDownLeft className="h-4 w-4 shrink-0 text-muted flip-rtl" aria-label={t('calls.incoming')} />;
  return <Phone className="h-4 w-4 shrink-0 text-muted" aria-hidden />;
}

/** New call: one person for a voice call, several for a group call. A page, as every form in the admin. */
export function NewCallPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const session = useCallSession();
  const [params] = useSearchParams();
  const { data: status } = useCallsStatus();
  const { data: people, isLoading, error, refetch } = useCallPeople();
  const [picked, setPicked] = useState<string[]>(() => (params.get('to') ? [params.get('to')!] : []));
  const [busy, setBusy] = useState(false);
  const pickerId = useId();
  const max = Math.max(1, (status?.settings.maxParticipants ?? 8) - 1);
  const call = async () => {
    setBusy(true);
    try {
      await session.start(picked);
      navigate('/calls', { replace: true });
    } catch (e) {
      toast.error(callErrorText(e, t));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormPage
      title={t('calls.newCall')}
      subtitle={t('calls.newCallSubtitle')}
      backTo="/calls"
      backLabel={t('calls.title')}
      submitLabel={picked.length > 1 ? t('calls.startGroupCall') : t('calls.startCall')}
      onSubmit={() => void call()}
      submitting={busy}
      disabled={picked.length === 0 || !status?.available || !!session.current}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
    >
      {/* Not a Field: that is a <label>, and a label around the chips' buttons would press them — a click on a
          name added the person, then the label pressed their chip's × and took them off again. */}
      <div>
        <label htmlFor={pickerId} className="mb-1.5 flex items-center gap-1 text-body-sm font-medium text-[var(--text-secondary)]">
          {t('calls.whoToCall')}
        </label>
        <PeoplePicker
          inputId={pickerId}
          people={people}
          value={picked}
          onChange={setPicked}
          max={max}
          fullLabel={t('calls.full', { n: max + 1 })}
        />
        <span className="mt-1 block text-caption text-faint">{t('calls.whoToCallHint', { n: max })}</span>
      </div>
      <p className="mt-4 flex items-center gap-2 text-body-sm text-error-fg dark:text-error-dark">
        <span className="h-2 w-2 rounded-full bg-error" aria-hidden />
        {t('calls.recordedNotice')}
      </p>
      {session.current && <p className="mt-2 text-body-sm text-muted">{t('calls.error.alreadyInCall')}</p>}
    </FormPage>
  );
}

/** Add people to the call you are in — the same call, the same recording. */
export function AddToCallPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const session = useCallSession();
  const { data: status } = useCallsStatus();
  const { data: people, isLoading, error, refetch } = useCallPeople();
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const pickerId = useId();
  const call = session.current;
  const on = new Set(call?.participants.filter((p) => p.status === 'ACCEPTED' || p.status === 'RINGING').map((p) => p.user.id) ?? []);
  const free = Math.max(0, (status?.settings.maxParticipants ?? 8) - on.size);
  const add = async () => {
    if (!call) return;
    setBusy(true);
    try {
      await callsApi.invite(call.id, picked);
      toast.success(t('calls.added'));
      navigate(-1);
    } catch (e) {
      toast.error(callErrorText(e, t));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormPage
      title={t('calls.addPeople')}
      subtitle={t('calls.addPeopleSubtitle')}
      backLabel={t('common.back')}
      submitLabel={t('calls.add')}
      onSubmit={() => void add()}
      submitting={busy}
      disabled={!call || picked.length === 0 || !call.me.mayAddPeople}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
    >
      {!call ? (
        <p className="text-body-sm text-muted">{t('calls.notInCall')}</p>
      ) : (
        // Not a Field, as on New call: a label around the chips' buttons would press them.
        <div>
          <label htmlFor={pickerId} className="mb-1.5 flex items-center gap-1 text-body-sm font-medium text-[var(--text-secondary)]">
            {t('calls.whoToAdd')}
          </label>
          <PeoplePicker
            inputId={pickerId}
            people={people?.filter((p) => !on.has(p.id))}
            value={picked}
            onChange={setPicked}
            max={Math.max(1, free)}
            fullLabel={t('calls.full', { n: status?.settings.maxParticipants ?? 8 })}
          />
          <span className="mt-1 block text-caption text-faint">{t('calls.whoToCallHint', { n: free })}</span>
        </div>
      )}
    </FormPage>
  );
}

export function useCallDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['calls', 'detail', id],
    queryFn: () => callsApi.detail(id!),
    enabled: !!id,
    refetchInterval: (q) => {
      const shown = q.state.data?.recording.shown;
      // Still being made or checked: look again until it settles.
      return shown === 'RECORDING' || shown === 'PROCESSING' ? 5_000 : false;
    },
  });
}
